import { isArray } from 'util';

import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { ENDPOINTS } from '@philips-emr/hpms-health-insurance-commons';
import { firstDayOfCurrentMonth, getMoment, ISO_FORMAT, lastDayOfCurrentMonth, shouldRenderMobile } from '@philips-emr/hpms-health-insurance-components';
import { DatePickerAPI, DeckAPI, FetchAPI, FormAPI, PanelAPI, Stamp, StampIcon } from '@philips-emr/tws-components';
import { Color } from '@philips-emr/tws-constants';
import Controller, { OnBeforeFetch, OnChange, OnClick, OnDynamicContentRender, OnMount, OnNavigate, OnSelectItem, OnUnmount } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, ClickEvent, FetchEvent, MountEvent, RenderEvent, SelectItemEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService } from '@philips-emr/tws-core/lib/services';

import { FragmentNames } from '../../codegen';
import { TechnicalVisitAliases } from '../../codegen/constants/aliases/technical-visit-aliases';
import { ROUTE } from '../../route';
import FilterService from '../../services/filter-service';
import BaseController from '../BaseController';

interface TechnicalVisit {
  ieStatus: number;
  dsStatus: string;
  vlDominio: string;
  nrSeqVisit: number;
}

interface DatePickerEventsData {
  date?: string;
  end?: string;
  id: string;
  status: string;
  subtitle?: string;
  title: string;
}

interface DatePickerData {
  active: boolean;
  busyPercent: number;
  visitDate: string;
  end?: string;
  events: DatePickerEventsData[];
  id: string;
}

export interface DatePickerNavigate {
  from: string[];
  to: string[];
}

interface Form {
  datepicker: string;
  beginDate:string;
  endDate: string;
  date: string;
  responsibleAudit: boolean;
}

@Controller({ fragment: FragmentNames.TECHNICAL_VISIT })
export default class TechnicalVisitController extends BaseController {

  @Inject(TechnicalVisitAliases.FETCH_VISIT_LIST_DESKTOP)
  private visitListFetch: FetchAPI;

  @Inject(TechnicalVisitAliases.FETCH_VISIT_LIST_MOBILE)
  private visitListFetchMobile: FetchAPI;

  @Inject(TechnicalVisitAliases.DATE_PICKER)
  private datePicker: DatePickerAPI;

  @Inject(TechnicalVisitAliases.DATE_PICKER_MOBILE)
  private datePickerMobile: DatePickerAPI;

  @Inject(TechnicalVisitAliases.PANEL_DESKTOP)
  private panelDesktop: PanelAPI;

  @Inject(TechnicalVisitAliases.PANEL_MOBILE)
  private panelMobile: PanelAPI;
  @OnMount(TechnicalVisitAliases.LAYOUT_TECHNICAL_VISIT)
  onMountDesktop(): void {
    if (shouldRenderMobile()) {
      this.panelMobile.setVisible(true);
      this.panelDesktop.setVisible(false);
    } else {
      this.panelMobile.setVisible(false);
    }
  }

  @OnDynamicContentRender([TechnicalVisitAliases.HPMS_DYNAMIC_CONTENT_FIELD, TechnicalVisitAliases.HPMS_DYNAMIC_CONTENT_FIELD_MOBILE])
  public onDynamicContentRender(event: RenderEvent): void {
    const { data, render } = event;
    const { ieStatus, dsStatus } = data as unknown as TechnicalVisit;
    const stamp = this.getStamp(ieStatus, dsStatus);

    if (stamp) {
      render((): ReactElement => stamp);
    }
  }

  @OnChange([TechnicalVisitAliases.DATE_PICKER, TechnicalVisitAliases.DATE_PICKER_MOBILE])
  onChangeDate(event: ChangeEvent<any>): void {
    const date = event.value;
    const api = event.target as DatePickerAPI;
    const fetch = shouldRenderMobile() ? this.visitListFetchMobile : this.visitListFetch;
    const oldParameters = fetch.getParameters();
    fetch.setParameters({ ...oldParameters, date });
    fetch.forceFetch();
    api.setValue(date);
  }

  @OnChange(TechnicalVisitAliases.FIELD_STATUS)
  onChangeFieldStatus(event: ChangeEvent<any>): void {
    const status = event.value;
    const statusList: String[] = [];

    if (isArray(status)) {
      status.forEach((status: TechnicalVisit) => {
        statusList.push(status.vlDominio);
      });
    }

    const oldParameters = this.visitListFetch.getParameters();
    this.visitListFetch.setParameters({ ...oldParameters, status: statusList });
    this.visitListFetch.forceFetch();
  }

  @OnMount([TechnicalVisitAliases.FETCH_VISIT_LIST_DESKTOP, TechnicalVisitAliases.FETCH_VISIT_LIST_MOBILE])
  onMountFetch(event: MountEvent): void {
    FilterService.restoreFetchState(event);
  }

  @OnMount(TechnicalVisitAliases.FILTER_TECHNICAL_VISIT)
  onMountFilter(event: MountEvent): void {
    FilterService.restoreFilterState(event);
  }

  @OnMount([TechnicalVisitAliases.FORM_DATE_PICKER, TechnicalVisitAliases.FORM_DATE_PICKER_MOBILE])
  onMountForm(event: MountEvent): void {
    FilterService.restoreFormState(event);
    const formApi = event.target as FormAPI;

    if (shouldRenderMobile()) {
      this.renderScreen(formApi, this.datePickerMobile);
    } else {
      this.renderScreen(formApi, this.datePicker);
    }
  }

  @OnUnmount([TechnicalVisitAliases.FORM_DATE_PICKER, TechnicalVisitAliases.FETCH_VISIT_LIST_DESKTOP])
  onUnmountDesktop(event: MountEvent): void {
    FilterService.persistState(event);
  }

  @OnUnmount([TechnicalVisitAliases.FORM_DATE_PICKER_MOBILE, TechnicalVisitAliases.FETCH_VISIT_LIST_MOBILE, TechnicalVisitAliases.FILTER_TECHNICAL_VISIT])
  onUnmountMobile(event: MountEvent): void {
    FilterService.persistState(event);
  }

  @OnChange(TechnicalVisitAliases.FIELD_CHECKBOX)
  onChangeCheckbox(event: ChangeEvent<any>): void {
    const audit = event.value;
    const oldParameters = this.visitListFetch.getParameters();
    this.visitListFetch.setParameters({ ...oldParameters, responsible: audit });
    this.visitListFetch.forceFetch();
  }

  @OnNavigate([TechnicalVisitAliases.DATE_PICKER, TechnicalVisitAliases.DATE_PICKER_MOBILE])
  handleDatePickerNavigate(navigate: DatePickerNavigate): void {
    const api = shouldRenderMobile() ? this.datePickerMobile : this.datePicker;
    this.updateDatePickerAvailableDates(ISO_FORMAT(navigate.to[0]), ISO_FORMAT(navigate.to[1]), api);
  }

  @OnBeforeFetch(TechnicalVisitAliases.FETCH_VISIT_LIST_MOBILE)
  onBeforeFetchMobile(event: FetchEvent): void {
    const api = event.target as FetchAPI;
    const date = this.datePickerMobile ? this.datePickerMobile.getValue() : undefined;
    const oldParams = api.getParameters();
    api.setParameters({ ...oldParams, date });
  }

  @OnSelectItem(TechnicalVisitAliases.DECK_VISIT_LIST)
  onSelectItem(event: SelectItemEvent<TechnicalVisit>): void {
    const { nrSeqVisit } = event.item;
    const api = event.target as unknown as DeckAPI;
    api.unselectAll();
    this.historyService.push(ROUTE.TECHNICAL_VISIT_DETAILS, { nrSeqVisit });
  }

  @OnClick(TechnicalVisitAliases.DECK_VISIT_LIST_MOBILE)
  onClickDetail(event: ClickEvent): void {
    if (event.value) {
      const { nrSeqVisit } = event.value;
      this.historyService.push(ROUTE.TECHNICAL_VISIT_DETAILS, { nrSeqVisit });
    }
  }

  private renderScreen(formApi: FormAPI, datepickerApi: DatePickerAPI): void {
    const { datepicker } = formApi.getRecord() as unknown as Form;

    const date = ISO_FORMAT(datepicker);
    datepickerApi.setValue(date);
    const beginDate = firstDayOfCurrentMonth(date);
    const endDate = lastDayOfCurrentMonth(date);

    this.updateDatePickerAvailableDates( beginDate, endDate, datepickerApi);
  }

  private updateDatePickerAvailableDates(beginDate: string, endDate: string, api: DatePickerAPI): void {
    void HttpService.get(ENDPOINTS.GET.TECHNICAL_VISIT, {
      date: beginDate, endDate,
    }).then((response) => {
      if (response.body) {
        const availableDays = response.body as [];
        const props = [] as object[];

        availableDays.forEach((day: DatePickerData) => {

          const event: DatePickerEventsData = {
            id: day.visitDate,
            status: 'confirmed',
            title: day.visitDate,
          };

          if (day.visitDate) {
            props.push({
              date: getMoment(day.visitDate).toJSON(),
              active: true,
              busyPercent: 0,
              id: getMoment(day.visitDate).toJSON(),
              events: [event],
            });
          }
        });

        if (api) {
          api.setData(props);
        }
      }
    });
  }

  private getStamp(ieStatus: number, dsStatus: string): ReactElement | null {
    let stampProps = null;
    switch (ieStatus) {
      case 1:
        stampProps = { color: Color.SAFETY_NEUTRAL, icon: StampIcon.CLOCK, label: dsStatus };
        break;
      case 2:
        stampProps = { color: Color.SAFETY_YELLOW, icon: StampIcon.WARNING, label: dsStatus };
        break;
      case 3:
        stampProps = { color: Color.SAFETY_BLUE, icon: StampIcon.CHECK, label: dsStatus };
        break;
      case 4:
        stampProps = { color: Color.SAFETY_GREEN, icon: StampIcon.CHECK, label: dsStatus };
        break;
      case 5:
        stampProps = { color: Color.SAFETY_NEUTRAL, icon: StampIcon.CANCELED, label: dsStatus };
        break;
      case 6:
        stampProps = { color: Color.SAFETY_BLUE, icon: StampIcon.CLOCK, label: dsStatus };
        break;
      case 7:
        stampProps = { className: 'c-form-stamp__stamp--purple', icon: StampIcon.CLOCK, label: dsStatus };
        break;
      case 8:
        stampProps = { color: Color.SAFETY_ORANGE, icon: StampIcon.WARNING, label: dsStatus };
        break;
      default:
        return null;
    }

    return <Stamp {...stampProps} />;
  }
}
